import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getExamsForStudent, type ExamConfig, type QuestionType } from '../config/regionConfig';
import { GraduationCap, ChevronRight, FileQuestion, Clock, Award } from 'lucide-react';
import { Card } from '@branon-shared/ui/Card';
import { IconBadge } from '@branon-shared/ui/IconBadge';
import { EmptyState } from '@branon-shared/ui/EmptyState';

const TYPE_LABELS: Record<QuestionType, string> = {
  mcq_single: 'MCQ',
  mcq_multi: 'Multi-MCQ',
  numerical: 'Numerical',
  assertion_reasoning: 'A-R',
  short_answer: 'Short Answer',
  long_answer: 'Long Answer',
  case_based: 'Case-Based',
};

const BADGE_TONES: Array<'coral' | 'marigold' | 'night'> = ['coral', 'marigold', 'night'];

function getQuestionTypeLabels(exam: ExamConfig): string[] {
  const types = new Set(exam.blueprint.sections.map(s => TYPE_LABELS[s.questionType]));
  return [...types];
}

function getTotalQuestions(exam: ExamConfig): number {
  return exam.blueprint.sections.reduce((sum, s) => sum + s.count, 0);
}

export default function PracticeTestHome() {
  const { session, isLoading } = useAuth();

  if (isLoading || !session) return null;

  const exams = getExamsForStudent(session.grade_level, session.board);

  return (
    <main className="max-w-2xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="mb-6">
        <h1 className="font-quicksand text-2xl sm:text-3xl font-bold text-ink mb-1">
          Exam Simulator
        </h1>
        <p className="text-gray-500 text-sm">
          Grade {session.grade_level}{session.board ? ` — ${session.board}` : ''} — Choose your target exam
        </p>
      </div>

      {exams.length === 0 ? (
        <Card>
          <EmptyState icon={GraduationCap} title={`No exams configured for Grade ${session.grade_level}.`} />
        </Card>
      ) : (
        <div className="space-y-3">
          {exams.map((exam: ExamConfig, idx: number) => {
            const typeLabels = getQuestionTypeLabels(exam);
            const totalQ = getTotalQuestions(exam);
            return (
              <Link key={exam.name} to={`/practice-test/${encodeURIComponent(exam.name)}`}>
                <Card className="hover:-translate-y-0.5 transition-transform" style={{ cursor: 'pointer' }}>
                  <div className="flex items-center gap-4">
                    <IconBadge icon={GraduationCap} tone={BADGE_TONES[idx % BADGE_TONES.length]} rotate={idx % 2 === 0 ? -4 : 4} />
                    <div className="flex-1 min-w-0">
                      <h2 className="font-quicksand text-base font-bold text-ink">{exam.name}</h2>
                      <p className="text-xs text-gray-500 mt-0.5">{exam.description}</p>

                      <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <FileQuestion className="w-3.5 h-3.5" />
                          {totalQ} questions
                        </span>
                        <span className="flex items-center gap-1">
                          <Award className="w-3.5 h-3.5" />
                          {exam.blueprint.totalMarks} marks
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {exam.blueprint.durationMinutes} min
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {typeLabels.map(label => (
                          <span key={label} className="px-2 py-0.5 bg-coral/10 text-coral rounded-full text-xs font-medium">
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </main>
  );
}
